/**
 * Area "Stato richiesta" — PURE view mapping of the BE effectiveStatus
 * (persisted + derived vocabulary, PR #864) to applicant-facing copy.
 */

export interface AreaStatusView {
  label: string;
  tone: 'info' | 'success' | 'warning' | 'neutral';
  description: string;
}

const VIEWS: Record<string, AreaStatusView> = {
  DRAFT: {
    label: 'Bozza',
    tone: 'neutral',
    description: 'La richiesta è in compilazione. Puoi riprenderla quando vuoi.',
  },
  DATA_IN_PROGRESS: {
    label: 'Compilazione in corso',
    tone: 'info',
    description: 'Stai completando i dati della richiesta. La bozza è salvata automaticamente.',
  },
  DOCUMENTS_PENDING: {
    label: 'Documenti da caricare',
    tone: 'info',
    description: 'I dati principali sono completi: mancano i documenti.',
  },
  SUBMITTED: {
    label: 'Inviata al controllo preliminare',
    tone: 'info',
    description: 'Aurino Broker sta verificando dati e documenti. Ti avviseremo via email.',
  },
  IN_REVIEW: {
    label: 'In verifica',
    tone: 'info',
    description: 'La richiesta è in lavorazione dal backoffice.',
  },
  NEEDS_CORRECTION: {
    label: 'Correzioni richieste',
    tone: 'warning',
    description: 'Alcuni dati o documenti vanno corretti: controlla l\'email ricevuta.',
  },
  PREAUTHORIZED_FOR_IDENTITY: {
    label: 'Verifica identità autorizzata',
    tone: 'info',
    description: 'Puoi procedere con la verifica dell\'identità dalla sezione dedicata.',
  },
  IDENTITY_IN_PROGRESS: {
    label: 'Verifica identità in corso',
    tone: 'info',
    description: 'Completa la verifica identità avviata.',
  },
  IDENTITY_VERIFIED: {
    label: 'Identità verificata',
    tone: 'success',
    description: 'La verifica identità è andata a buon fine: la richiesta passa alla valutazione finale.',
  },
  FINAL_REVIEW: {
    label: 'Valutazione finale',
    tone: 'info',
    description: 'La richiesta è in valutazione finale presso Aurino Broker.',
  },
  APPROVED: {
    label: 'Approvata',
    tone: 'success',
    description: 'La richiesta è stata approvata: riceverai le istruzioni di attivazione.',
  },
  CONVERTED: {
    label: 'Attivata',
    tone: 'success',
    description: 'Il tuo profilo operativo è stato creato.',
  },
  REJECTED: {
    label: 'Respinta',
    tone: 'warning',
    description: 'La richiesta è stata respinta. Trovi i dettagli nell\'email ricevuta.',
  },
  CANCELLED: {
    label: 'Annullata',
    tone: 'neutral',
    description: 'La richiesta è stata annullata dal backoffice.',
  },
  WITHDRAWN: {
    label: 'Ritirata',
    tone: 'neutral',
    description: 'Hai ritirato la richiesta. Puoi aprirne una nuova quando vuoi.',
  },
  EXPIRED: {
    label: 'Scaduta',
    tone: 'neutral',
    description: 'La bozza è scaduta per inattività. Puoi aprirne una nuova.',
  },
};

const FALLBACK: AreaStatusView = {
  label: 'In lavorazione',
  tone: 'info',
  description: 'La richiesta è in lavorazione.',
};

export function areaStatusView(effectiveStatus: string): AreaStatusView {
  return VIEWS[effectiveStatus] ?? FALLBACK;
}

/** Statuses from which the applicant may withdraw (mirror of the BE lifecycle). */
export function canWithdraw(status: string): boolean {
  return status === 'DRAFT' || status === 'SUBMITTED' || status === 'NEEDS_CORRECTION';
}

// ─── REJECTED cooldown (review fix #6) ───────────────────────────────────────
//
// A REJECTED request carries the 6-month cooldown (lock D4): the area must
// show the EXACT restart date and keep the new-request CTA hidden/disabled
// until it passes — never the generic "puoi aprirne una nuova quando vuoi",
// and never a doomed call the backend would refuse anyway.

export interface RejectedCooldownView {
  /** True while a new request must not be offered. */
  blocked: boolean;
  /** DD/MM/YYYY restart date, when known. */
  dateLabel: string | null;
}

export function rejectedCooldownView(
  cooldownUntil: string | null,
  now: number,
): RejectedCooldownView {
  if (!cooldownUntil) {
    // Legacy rejection without the materialised stamp: the BE still enforces
    // the identity-keyed cooldown, so the FE stays conservative.
    return { blocked: true, dateLabel: null };
  }
  const until = new Date(cooldownUntil);
  if (Number.isNaN(until.getTime())) return { blocked: true, dateLabel: null };
  const dd = String(until.getDate()).padStart(2, '0');
  const mm = String(until.getMonth() + 1).padStart(2, '0');
  const dateLabel = `${dd}/${mm}/${until.getFullYear()}`;
  return { blocked: until.getTime() > now, dateLabel };
}
